/**
 * LiveSiteHeader — small banner shown at the top of every WP-management
 * panel so the user always knows exactly which live WordPress site this
 * panel is writing to. Source: /api/bridge-proxy/config/{org_id}.
 *
 * `surfaceLabel` is shown after the URL in muted text, e.g.
 *   "Editing live site: https://example.com  ·  Menus"
 * so the relationship between the OL panel and the WP surface is
 * unambiguous.
 *
 * `wpPath` (optional) appends a deep-link to a specific path on the
 * live site, e.g. "/category/announcements" for the Categories panel.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveSiteHeader({ orgId, surfaceLabel, wpPath = '' }) {
  const [siteUrl, setSiteUrl] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/bridge-proxy/config/${orgId}`, { withCredentials: true })
      .then(({ data }) => setSiteUrl(data?.data?.site_url || data?.site_url || null))
      .catch(() => setSiteUrl(null));
  }, [orgId]);

  if (!siteUrl) {
    return (
      <div className="px-3 py-2 rounded bg-slate-50 border border-slate-200 text-xs text-slate-500"
           data-testid="live-site-header-loading">
        Looking up live WordPress site URL…
      </div>
    );
  }

  const fullUrl = siteUrl + (wpPath || '');

  return (
    <div className="flex items-center justify-between px-3 py-2 rounded bg-slate-50 border border-slate-200 text-xs gap-3"
         data-testid="live-site-header">
      <div className="min-w-0">
        <span className="text-slate-500">Editing live site:</span>{' '}
        <span className="font-mono text-slate-800 truncate inline-block max-w-md align-middle">{siteUrl}</span>
        {surfaceLabel && (
          <>
            <span className="text-slate-400 mx-2">·</span>
            <span className="text-slate-700 font-medium">{surfaceLabel}</span>
          </>
        )}
      </div>
      <a
        data-testid="live-site-header-link"
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 text-slate-700"
      >View on live site ↗</a>
    </div>
  );
}
